import {
    FetchState,
    HsabAccountDisplayType,
    HsabAccountNametag,
    HsabAccountsState,
    HsabAccountType,
    HsabAccountTypesResponse,
    HsaBankSsoLocationName,
    HsabPlanType,
    HsabSsoDestination,
    NotionalBalanceResult
} from "@/shared/types";
import router from "@/employee/routes";
import * as bankService from "@/shared/api/bankService";
import * as storeHelper from "@/shared/storeHelper";
import { Security } from "@/shared/route-names";
import {
    BiometricAuthSettings,
    Home,
    HsabAccountSelection,
    HsabNotionalDashboard,
    MyProfileDisplay,
    NotionalAccount,
    Settings,
    SsoLoginError,
    UserPreferences,
    WexRedirect
} from "@/employee/route-names";
import { putSsoLinkClick } from "@/shared/api/userService";
import {
    AppBrowserTarget,
    isMobileApp,
    openUrl
} from "@/shared/mobileHelper";
import Vue from "vue";
import { defineStore } from "pinia";
import _ from "lodash";
import moment, { Moment } from "moment";
import { useFeatureFlagStore } from "@/shared/store/featureFlag";
import { useUserStore } from "@/shared/store/user";
import { PlanTypesAndSubTypes } from "@/employee/Utilities/HsabExpenseCategoryUtility";
import { BankAccountHoldFunctionType } from "@/employer/types";

export const initialState: HsabAccountsState = {
    fetchState: FetchState.Uninitialized,
    accounts: [],
    employerName: null,
    userShouldBeSentToWex: false,
    planTypes: [],
    notionalAccountsFetchState: FetchState.Uninitialized,
    userHasNotionalAccounts: null,
    accountDisplayTypeFetchState: FetchState.Uninitialized,
    canPerformWithdrawalFunctionFetchState: FetchState.Uninitialized,
    canPerformWithdrawalFunction: true,
    accountDisplayType: {},
    notionalDashboardIsActive: false,
    allowedRoutes: [
        HsabNotionalDashboard,
        HsabAccountSelection,
        Settings,
        MyProfileDisplay,
        Security,
        UserPreferences,
        BiometricAuthSettings
    ],
    notionalBalances: {},
    notionalBalancesFetchState: {},
    userHasDebitCardAccess: false
};

function displayName (accountType: HsabAccountType): string {
    switch (accountType) {
        case HsabAccountType.None:
            return "—";

        default:
            return accountType.toUpperCase();
    }
}

const currentDateFormat = "DD-MMM-YY";

export const useHsabAccountsStore = defineStore("hsabAccounts", {
    persist: {
        storage: localStorage,
        paths: storeHelper.getStoragePaths("hsabAccounts"),
    },
    state: () => (_.cloneDeep(initialState)),
    getters: {
        allHsabAccounts: state => state.accounts,
        allActiveHsabAccounts: state => state.accounts.filter(x => x.isActive),
        allHsabAccountsLoaded: state => state.fetchState === FetchState.Loaded,
        allHsabAccountsLoading: state => state.fetchState === FetchState.Loading,
        hsabAccountsFailedToLoad: state => state.fetchState === FetchState.Failed,
        notionalAccounts: state => state.accounts.filter(t => t.bankAccountType === HsabAccountType.Notional),
        hsaAccounts: state => state.accounts.filter(t => t.bankAccountType === HsabAccountType.Hsa),
        bankAccountTypeDisplayName: state => displayName,
        userHasNotionalAccountsLoaded: state => state.notionalAccountsFetchState === FetchState.Loaded,
        userHasAnyNotionalAccounts: state => state.accounts.some(a => a.bankAccountType === HsabAccountType.Notional),
        userHasAnyActiveAccount: state => state.accounts.some(a => a.isActive),
        userHasAnyInactiveAccount: state => state.accounts.some(a => !a.isActive),
        userHasActiveNotionalAccounts: state =>
            state.accounts.some(a => a.isActive && a.bankAccountType === HsabAccountType.Notional),
        userHasActiveHsaAccounts: state =>
            state.accounts.some(a => a.isActive && a.bankAccountType === HsabAccountType.Hsa),
        userHasHsaAccount: state => state.accounts.some(a => a.bankAccountType === HsabAccountType.Hsa),
        canPerformWithdrawal: state => state.canPerformWithdrawalFunction,
        isAccountDisplayTypeLoaded: state => state.accountDisplayTypeFetchState === FetchState.Loaded,
        isHsaOnly () {
            return this.userHasHsaAccount
              && !this.userHasActiveNotionalAccounts;
        },
        isNotionalOnly () {
            return !this.userHasHsaAccount
              && this.userHasActiveNotionalAccounts;
        },
        isWexOnlyAccount () {
            return this.isAccountDisplayTypeLoaded &&
                this.accountDisplayType.isWexOnlyAccount;
        },
        isHsabAccountSelectionVisible () {
            return this.isAccountDisplayTypeLoaded &&
                this.accountDisplayType.isHsabAccountSelectionVisible;
        },
        isNotionalAccountPageEnabled () {
            const featureFlagStore = useFeatureFlagStore();
            return featureFlagStore.flags.bend15789NotionalTransactions;
        },
        redirectToNotionalDashboard () {
            return this.accountDisplayType &&
                this.accountDisplayType.notionalDashboardRedirect;
        },
        notionalAllowedRoutes: state => state.allowedRoutes,
        accountsFetchState: state => state.fetchState,
        allNotionalBalances: state => state.notionalBalances,
        notionalBalancesLoaded: state => accountId => state.notionalBalancesFetchState[accountId] === FetchState.Loaded,
        findNotionalAccount: state => (planName: string, startDate: Moment) => {
            return state.accounts.find(x => x.accountName === planName && moment(x.startDate).isSame(startDate));
        },
        findHsaAccount: state => () => {
            return _.find(state.accounts, x => x.bankAccountType === HsabAccountType.Hsa);
        },
        allActivePlanTypesAndSubTypes: state => {
            const activeAccounts = state.accounts.filter(x => x.isActive);
            const planTypes = activeAccounts.map(x => x.planType);
            const planSubTypes = activeAccounts.map(x => x.planSubType);
            if (activeAccounts.some(a => a.bankAccountType === HsabAccountType.Hsa)) {
                planTypes.push(HsabPlanType.HealthSavingAccount);
            }
            return {
                types: planTypes,
                subTypes: planSubTypes,
                hasMedicalPlan: activeAccounts.some(a => a.planType === HsabPlanType.MedicalFlex ||
                    a.planType === HsabPlanType.HealthReimbursementAccount ||
                    a.planType === HsabPlanType.HealthSavingAccount),
                allowHealthcareClaims: activeAccounts.some(a => a.allowHealthcareClaims === true),
                hasSmartCommutePlan: activeAccounts.some(a => a.isSmartCommutePlan === true)
            } as PlanTypesAndSubTypes;
        },
        debitCardsAvailable () {
            if (!this.allHsabAccountsLoaded) {
                return false;
            }
            return this.userHasDebitCardAccess;
        }
    },
    actions: {
        setLoading () {
            this.fetchState = FetchState.Loading;
        },
        loadingFailed () {
            this.fetchState = FetchState.Loaded;
            this.accounts = null;
        },
        setHsabAccounts (data: HsabAccountTypesResponse) {
            this.accounts = data.accounts;
            this.employerName = data.employerName;
            this.userShouldBeSentToWex = data.userShouldBeSentToWex;
            this.userHasDebitCardAccess = data.userHasDebitCardAccess;
            this.planTypes = data.planTypes;
            this.fetchState = FetchState.Loaded;
        },
        setCanPerformWithdrawalFunctionLoading () {
          this.canPerformWithdrawalFunctionFetchState = FetchState.Loading;
        },
        setCanPerformWithdrawalFunction (data: boolean) {
            this.canPerformWithdrawalFunction = data;
            this.canPerformWithdrawalFunctionFetchState = FetchState.Loaded;
        },
        setAccountDisplayTypeLoading () {
            this.accountDisplayTypeFetchState = FetchState.Loading;
        },
        setAccountDisplayType (data: HsabAccountDisplayType) {
            this.accountDisplayType = data;
            this.accountDisplayTypeFetchState = FetchState.Loaded;
        },
        setAccountDisplayTypeFailed () {
            this.accountDisplayTypeFetchState = FetchState.Loaded;
            this.accountDisplayType = {};
        },
        setNotionalBalances (params: { accountId: number, data: NotionalBalanceResult }) {
            params.data.records = params.data.records.map(x => {
                const momentInst = moment(x.date);
                return {
                    balance: x.balance,
                    timestamp: momentInst.unix(),
                    date: momentInst.format(currentDateFormat)
                };
            });
            Vue.set(this.notionalBalances, params.accountId, params.data);
            Vue.set(this.notionalBalancesFetchState, params.accountId, FetchState.Loaded);
        },
        setNotionalBalancesFetchState (params: { accountId: number, fetchState: FetchState }) {
            Vue.set(this.notionalBalancesFetchState, params.accountId, params.fetchState);
        },
        reloadHsabAccounts (): Promise<any> {
            this.fetchState = FetchState.Uninitialized;
            this.accounts = [];
            return this.getHsabAccounts();
        },
        adminReloadHsabAccounts (args?: { username: string, partnerId: number}): Promise<any> {
            this.fetchState = FetchState.Uninitialized;
            this.accounts = [];
            return this.getHsabAccounts(args);
        },
        getHsabAccounts (args?: { username: string, partnerId: number}): Promise<any> {
            if (this.fetchState !== FetchState.Uninitialized) {
                return Promise.resolve();
            }
            this.setLoading();
            return fetchHsabAccounts(args)
                .then(response => {
                    this.setHsabAccounts(response.data);
                })
                .catch(err => {
                    this.loadingFailed();
                    this.fetchState = FetchState.Failed;
                    return storeHelper.handleError({ error: err, rethrow: true });
                });
        },

        navigateToAccountPage (account: HsabAccountNametag): Promise<any> {
            const userStore = useUserStore();
            if (account.bankAccountType === HsabAccountType.Hsa) {
                userStore.setIsNotionalDashboardActive(false);
                router.push({ name: Home });
            }
            if (account.bankAccountType === HsabAccountType.Notional && this.isNotionalAccountPageEnabled) {
                router.push({ name: NotionalAccount, params: { accountId: account.accountId.toString() } });
            } else if (account.bankAccountType === HsabAccountType.Notional) {
                userStore.setIsNotionalDashboardActive(true);
                router.push({ name: HsabNotionalDashboard });
            }
            return Promise.resolve();
        },
        getNotionalBalances (accountId: number): Promise<any> {
            this.setNotionalBalancesFetchState({ accountId, fetchState: FetchState.Loading });
            return bankService.getNotionalBalances(accountId)
                .then(response => {
                    this.setNotionalBalances({ accountId, data: response.data});
                })
                .catch(err => {
                    this.setNotionalBalancesFetchState({ accountId, fetchState: FetchState.Uninitialized });
                    return storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        getHsabAccountDisplayType (): Promise<any> {
            if (this.accountDisplayTypeFetchState !== FetchState.Uninitialized) {
                return Promise.resolve();
            }

            this.setAccountDisplayTypeLoading();
            return bankService.getHsabAccountDisplayType()
                .then(response => {
                    this.setAccountDisplayType(response.data);
                })
                .catch(err => {
                    this.setAccountDisplayTypeFailed();
                    return storeHelper.handleError({error: err, rethrow: true});
                });
        },
        loadCanPerformWithdrawalFunction (): Promise<any> {
            if (this.canPerformWithdrawalFunctionFetchState !== FetchState.Uninitialized) {
                return Promise.resolve();
            }
            this.setCanPerformWithdrawalFunctionLoading();
            return bankService.canPerformHoldFunction(BankAccountHoldFunctionType.Withdrawals)
                .then(response => {
                    this.setCanPerformWithdrawalFunction(response.data);
                })
                .catch(err => {
                    return storeHelper.handleError({error: err, rethrow: true});
                });
        },
        navigateToWex (): Promise<any> {
            const location: HsaBankSsoLocationName = {
                locationName: HsabSsoDestination.Home,
                isMobileApp: isMobileApp(),
                urlOfPageWithButton: router.currentRoute.path
              };

            return putSsoLinkClick(location)
                .then(response => {
                    if (!response || !response.data || !response.data.samlProviderUrl) {
                        throw new Error(("SsoClick response data didn't look right, go to error handling"));
                    }

                    if (isMobileApp()) {
                        const options = "location=yes,hideurlbar=yes,toolbarposition=top,fullscreen=no";
                        openUrl(response.data.samlProviderUrl, AppBrowserTarget.blank, options);
                        router.push({ name: WexRedirect });

                    } else {
                        window.open(response.data.samlProviderUrl, "_self");
                    }

                    return Promise.resolve();
                })
                .catch(err => {
                    router.push({ name: SsoLoginError });
                    return Promise.resolve();
                });
        }
    }
});

function fetchHsabAccounts (args: { username: string, partnerId: number}) {
    const userStore = useUserStore();
    return userStore.isAdminPortal
        ? bankService.getHsabAccountTypesAdmin(args)
        : bankService.getHsabAccountTypes(args);
}